import { useState } from "react";
import { Upload, Loader2, Plus, Trash2, ArrowUp, ArrowDown, X } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { uploadImage, resolveImg } from "@/lib/apiData";

const inputCls = "mt-1 h-11 bg-[#fbf9f7] border-neutral-300 rounded-none focus-visible:ring-[#d00000]";

export const ImageField = ({ value, onChange, testId }) => {
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const res = await uploadImage(file);
      onChange(res.url);
      toast.success("Image uploaded");
    } catch {
      toast.error("Upload failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="mt-1 flex items-center gap-4">
      {value ? <img src={resolveImg(value)} alt="preview" className="w-24 h-16 object-cover border border-neutral-200" data-testid={`${testId}-preview`} />
        : <div className="w-24 h-16 bg-neutral-100 border border-dashed border-neutral-300 flex items-center justify-center text-neutral-400 text-xs">No image</div>}
      <label className="inline-flex items-center gap-2 h-10 px-4 border border-neutral-300 cursor-pointer hover:border-[#d00000] text-sm text-neutral-700 transition-colors">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
        {value ? "Change image" : "Upload image"}
        <input type="file" accept="image/*" className="hidden" data-testid={testId} onChange={(e) => upload(e.target.files[0])} />
      </label>
      {value && <button type="button" onClick={() => onChange("")} className="text-neutral-400 hover:text-[#d00000]" aria-label="Remove image" data-testid={`${testId}-remove`}><X className="w-4 h-4" /></button>}
    </div>
  );
};

const emptyItem = (fields) => Object.fromEntries(fields.map((f) => [f.name, f.type === "checkbox" ? true : f.type === "list" || f.type === "lines" || f.type === "multicheck" ? [] : ""]));
const registry = {};

const ListField = ({ field, value = [], onChange, options, testId }) => {
  const Row = registry.FieldRow;
  const set = (i, v) => onChange(value.map((it, k) => (k === i ? v : it)));
  const move = (i, d) => {
    const next = [...value];
    [next[i], next[i + d]] = [next[i + d], next[i]];
    onChange(next);
  };
  return (
    <div className="mt-2 space-y-3" data-testid={testId}>
      {value.map((item, i) => (
        <div key={i} className="border border-neutral-200 bg-[#fbf9f7] p-4" data-testid={`${testId}-item-${i}`}>
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs uppercase tracking-widest text-neutral-400">{field.itemLabel || "Item"} {i + 1}</span>
            <div className="flex gap-1">
              <button type="button" disabled={i === 0} onClick={() => move(i, -1)} className="p-1 text-neutral-400 hover:text-neutral-900 disabled:opacity-30" aria-label="Move up"><ArrowUp className="w-4 h-4" /></button>
              <button type="button" disabled={i === value.length - 1} onClick={() => move(i, 1)} className="p-1 text-neutral-400 hover:text-neutral-900 disabled:opacity-30" aria-label="Move down"><ArrowDown className="w-4 h-4" /></button>
              <button type="button" onClick={() => onChange(value.filter((_, k) => k !== i))} className="p-1 text-neutral-400 hover:text-[#d00000]" data-testid={`${testId}-remove-${i}`} aria-label="Remove"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            {field.fields.map((sub) => (
              <div key={sub.name} className={["textarea", "list", "image", "lines"].includes(sub.type) ? "sm:col-span-2" : ""}>
                <Row field={sub} value={item[sub.name]} options={options} testId={`${testId}-${i}-${sub.name}`} onChange={(v) => set(i, { ...item, [sub.name]: v })} />
              </div>
            ))}
          </div>
        </div>
      ))}
      <button type="button" onClick={() => onChange([...value, emptyItem(field.fields)])} data-testid={`${testId}-add`}
        className="inline-flex items-center gap-2 h-10 px-4 border border-dashed border-neutral-300 text-sm text-neutral-700 hover:border-[#d00000] hover:text-[#d00000] transition-colors">
        <Plus className="w-4 h-4" /> Add {field.itemLabel?.toLowerCase() || "item"}
      </button>
    </div>
  );
};

const MultiCheck = ({ value = [], onChange, opts, testId }) => (
  <div className="mt-2 flex flex-wrap gap-2" data-testid={testId}>
    {opts.map((o) => {
      const on = value.includes(o.value);
      return (
        <button type="button" key={o.value} onClick={() => onChange(on ? value.filter((v) => v !== o.value) : [...value, o.value])} data-testid={`${testId}-${o.value}`}
          className={`px-3 h-9 text-sm border transition-colors ${on ? "bg-[#d00000] border-[#d00000] text-white" : "bg-white border-neutral-300 text-neutral-700 hover:border-[#d00000]"}`}>
          {o.label}
        </button>
      );
    })}
  </div>
);

export const FieldInput = ({ field, value, onChange, options = {}, testId }) => {
  const opts = typeof field.options === "string" ? options[field.options] || [] : field.options || [];
  switch (field.type) {
    case "textarea": return <Textarea value={value ?? ""} onChange={(e) => onChange(e.target.value)} rows={field.rows || 3} data-testid={testId} className="mt-1 bg-[#fbf9f7] border-neutral-300 rounded-none focus-visible:ring-[#d00000]" />;
    case "number": return <Input type="number" step="any" value={value ?? ""} onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))} data-testid={testId} className={inputCls} />;
    case "secret": return <Input type="password" autoComplete="new-password" value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder="Not set" data-testid={testId} className={inputCls} />;
    case "checkbox": return <div className="mt-2"><Switch checked={!!value} onCheckedChange={onChange} data-testid={testId} className="data-[state=checked]:bg-[#d00000]" /></div>;
    case "image": return <ImageField value={value} onChange={onChange} testId={testId} />;
    case "select": return (
      <select value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={testId} className="mt-1 w-full h-11 px-3 bg-[#fbf9f7] border border-neutral-300 rounded-none focus:outline-none focus:ring-2 focus:ring-[#d00000]">
        {opts.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    );
    case "tags": return <Input value={Array.isArray(value) ? value.join(", ") : value ?? ""} onChange={(e) => onChange(e.target.value.split(",").map((s) => s.trimStart()))} onBlur={(e) => onChange(e.target.value.split(",").map((s) => s.trim()).filter(Boolean))} data-testid={testId} className={inputCls} />;
    case "lines": return <Textarea value={(value || []).join("\n")} onChange={(e) => onChange(e.target.value.split("\n"))} onBlur={(e) => onChange(e.target.value.split("\n").map((s) => s.trim()).filter(Boolean))} rows={field.rows || 4} data-testid={testId} className="mt-1 bg-[#fbf9f7] border-neutral-300 rounded-none focus-visible:ring-[#d00000]" />;
    case "multicheck": return <MultiCheck value={value} onChange={onChange} opts={opts} testId={testId} />;
    case "list": return <ListField field={field} value={value} onChange={onChange} options={options} testId={testId} />;
    default: return <Input value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder={field.placeholder} data-testid={testId} className={inputCls} />;
  }
};

export const FieldRow = ({ field, value, onChange, options, testId }) => (
  <div>
    <label className="text-neutral-500 text-xs uppercase tracking-widest">{field.label}</label>
    {field.help && <p className="text-xs text-neutral-400 mt-0.5">{field.help}</p>}
    <FieldInput field={field} value={value} onChange={onChange} options={options} testId={testId} />
  </div>
);
registry.FieldRow = FieldRow;
